import { useMemo } from 'react'
import type { CategoriesData, CellsMap, CellMetaMap, SettingsData, BanksData } from '../types'

interface DailyBalance {
  date: string
  income: number
  expense: number
  balance: number
}

function safeISO(d: Date | undefined | null): string {
  if (!d || !(d instanceof Date) || isNaN(d.getTime())) return ''
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

/**
 * 日別の収支・残高を計算
 *
 * 優先順位:
 *   1. 口座別残高の合計（その日に1つでも口座残高入力があればその合計）
 *   2. openingBalances[iso]（日別の手入力）
 *   3. 前日の翌日繰越（自動引継ぎ）
 *
 * リスケ中のセル（cellMeta.status === 'rescheduled'）は収支計算から除外
 */
export function useBalanceCalc(
  categories: CategoriesData,
  cells: CellsMap,
  dates: Date[],
  settings: SettingsData,
  cellMeta: CellMetaMap = {},
  banks: BanksData | undefined = undefined
): DailyBalance[] {
  return useMemo(() => {
    const safeDates = (dates || []).filter((d): d is Date => d instanceof Date && !isNaN(d.getTime()))
    if (safeDates.length === 0) return []

    const initialBalance = Number(settings?.settings?.['initial_balance'] || '0')

    const incomeVendorIds = new Set<string>()
    const expenseVendorIds = new Set<string>()
    for (const cat of categories?.income || []) {
      for (const v of cat.vendors || []) incomeVendorIds.add(v.id)
    }
    for (const cat of categories?.expense || []) {
      for (const v of cat.vendors || []) expenseVendorIds.add(v.id)
    }

    const bankAccounts = banks?.accounts || []
    const bankBalances = banks?.balances || {}

    let prevClosing = initialBalance
    const result: DailyBalance[] = []

    for (const date of safeDates) {
      const dateStr = safeISO(date)

      // 前日繰越の決定
      // 1. 口座別残高合計
      let bankSum = 0
      let anyBank = false
      for (const acc of bankAccounts) {
        const bkey = `${acc.id}_${dateStr}`
        if (bankBalances[bkey] !== undefined) {
          bankSum += bankBalances[bkey]
          anyBank = true
        }
      }
      let opening: number
      if (anyBank) {
        opening = bankSum
      } else if (settings?.openingBalances?.[dateStr] !== undefined) {
        opening = settings.openingBalances[dateStr]
      } else {
        opening = prevClosing
      }

      // 収支集計（リスケ中セルは除外）
      let income = 0
      let expense = 0
      for (const [key, amount] of Object.entries(cells || {})) {
        if (!key.endsWith(`_${dateStr}`)) continue
        // リスケ中セルは計算から除外
        if (cellMeta[key]?.status === 'rescheduled') continue
        const vendorId = key.slice(0, -(dateStr.length + 1))
        if (incomeVendorIds.has(vendorId)) income += amount
        else if (expenseVendorIds.has(vendorId)) expense += amount
      }

      const closing = opening + income - expense
      result.push({ date: dateStr, income, expense, balance: closing })
      prevClosing = closing
    }

    return result
  }, [categories, cells, dates, settings, cellMeta, banks])
}
